/**
 * Effective-permissions selectors — pure functions that translate a
 * Member record (as returned by /accounts/{id}/members and already
 * loaded for the Members table) into an IAM-style verb + count +
 * resource-type list.
 *
 * Client-side aggregate only — no new coord API calls per SCK-470.
 * Copy tone lifted from the AWS IAM policy simulator: short imperative
 * verbs, exact counts, resource type spelled out.
 */

import { deriveDisplayRole } from "./selectors";

/**
 * Turn a per-project role string into a verb bucket.
 * PROJECT_ADMIN => 'admin'
 * CONTRIBUTOR   => 'deploy'
 * VIEWER        => 'view'
 * anything else => 'view' (safe default; matches read-only floor)
 */
function bucketForProjectRole(role) {
  switch ((role || "").toUpperCase()) {
    case "PROJECT_ADMIN":
      return "admin";
    case "CONTRIBUTOR":
      return "deploy";
    case "VIEWER":
      return "view";
    default:
      return "view";
  }
}

/**
 * deriveEffectivePermissions(member, memberList, currentUser)
 *   -> { role, statements: [{ verb, count, resource, detail? }] }
 *
 * `statements` is ordered from most-privileged to least so the drawer
 * reads top-down as "biggest thing this user can do first."
 *
 * The account role (OWNER/ADMIN/DEVELOPER/VIEWER/SYSTEM) drives the
 * account-scope statements; the member.projects array drives the
 * workspace-scope statements.
 */
export function deriveEffectivePermissions(member, memberList, currentUser) {
  if (!member) {
    return { role: "VIEWER", statements: [] };
  }

  const role = deriveDisplayRole(member, memberList, currentUser);
  const projects = Array.isArray(member.projects) ? member.projects : [];

  // Bucket workspaces by verb.
  const buckets = { admin: 0, deploy: 0, view: 0 };
  for (const p of projects) {
    buckets[bucketForProjectRole(p.role)] += 1;
  }

  const statements = [];

  // Account-scope verbs first (most privileged).
  if (role === "SYSTEM") {
    statements.push({
      verb: "Administer",
      count: null,
      resource: "the entire platform (SYSTEM)",
      detail: "Global superuser. Every read + write on every account.",
    });
  }
  if (role === "OWNER") {
    statements.push({
      verb: "Own",
      count: null,
      resource: "this account",
      detail:
        "Only OWNER can transfer or delete the account itself. Also inherits every Admin verb.",
    });
  }
  if (role === "OWNER" || role === "ADMIN") {
    statements.push({
      verb: "Manage",
      count: null,
      resource: "members + roles on this account",
    });
    statements.push({
      verb: "Read",
      count: null,
      resource: "billing + usage on this account",
    });
  }
  if (role === "BILLING") {
    statements.push({
      verb: "Read",
      count: null,
      resource: "billing + usage on this account",
    });
  }

  // Workspace create-grant (a distinct account-scope permission).
  if (member.canCreateProjects || role === "OWNER" || role === "ADMIN") {
    statements.push({
      verb: "Create",
      count: null,
      resource: "new workspaces in this account",
    });
  }

  // Per-project verbs, ordered admin -> deploy -> view.
  if (buckets.admin > 0) {
    statements.push({
      verb: "Administer",
      count: buckets.admin,
      resource: buckets.admin === 1 ? "workspace" : "workspaces",
      detail: "Full write on every app + config in these workspaces.",
    });
  }
  if (buckets.deploy > 0) {
    statements.push({
      verb: "Deploy to",
      count: buckets.deploy,
      resource: buckets.deploy === 1 ? "workspace" : "workspaces",
      detail: "Build + deploy + restart apps; cannot manage the workspace itself.",
    });
  }
  if (buckets.view > 0) {
    statements.push({
      verb: "View",
      count: buckets.view,
      resource: buckets.view === 1 ? "workspace" : "workspaces",
      detail: "Read-only — apps, builds, logs. No writes.",
    });
  }

  // Fallback so an empty-permissions user still reads as SOMETHING.
  if (statements.length === 0) {
    statements.push({
      verb: "View",
      count: null,
      resource: "their own account membership",
      detail: "No workspace grants yet.",
    });
  }

  return { role, statements };
}
